import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { ExecutionCreateResponse } from '@/features/decisions/scenarios.api.types';
import { useTenant } from '@/providers/TenantProvider';

export interface SubmitExecutionInput {
  scenarioRunId: string;
  configBody: unknown;
  submittedBy: string;
  // Sent as the Idempotency-Key header. The caller owns it, and must keep it stable across
  // retries of the same payload, because that is the only thing standing between a
  // timed-out request and a second simulation running at real compute cost.
  idempotencyKey: string;
}

export function useSubmitExecution() {
  const { tenantAlias } = useTenant();
  const queryClient = useQueryClient();

  return useMutation<ExecutionCreateResponse, Error, SubmitExecutionInput>({
    mutationFn: ({ scenarioRunId, configBody, submittedBy, idempotencyKey }) =>
      apiFetch<ExecutionCreateResponse>(tenantAlias, ApiEndpoint.ScenarioExecutionCreate, {
        pathParams: { scenarioRunId },
        headers: { 'Idempotency-Key': idempotencyKey },
        body: { config_body: configBody, submitted_by: submittedBy },
      }),
    // Deliberately no retry. Submitting starts an Argo workflow that costs real compute
    // and cannot be recalled, so an automatic retry is only safe if the idempotency key
    // reached the service -- and on a network error we cannot know that it did.
    retry: false,
    onSuccess: () => {
      // Prefix matches, so every cached page is refetched rather than only the one being
      // viewed: a new run changes both the list's run count and its status. Matching on
      // the endpoint alone, without the params, keeps this from depending on createKey's
      // internal tuple shape.
      for (const endpoint of [ApiEndpoint.ScenariosList, ApiEndpoint.ScenarioExecutionsList]) {
        void queryClient.invalidateQueries({
          queryKey: ['KSAI-DEEP-ENTERPRISE', tenantAlias, endpoint],
        });
      }
    },
  });
}
